import { useEffect, useRef } from 'react';
import { useLocation } from 'wouter';
import { loadGtag, trackPageView as trackGaPageView } from './gtag.ts';
import { loadPixel, trackPageView as trackPixelPageView } from './metaPixel.ts';

/**
 * Headless analytics mount. On first render (prod only) it injects the GA4 +
 * Meta Pixel loaders, then fires exactly one page_view per surface on every
 * wouter route change. Renders nothing. Must live inside <Router> so
 * useLocation() reflects SPA navigations.
 */
export function Analytics() {
  const [location] = useLocation();
  const initialized = useRef(false);

  useEffect(() => {
    // Never fire in the Vite dev server, so local traffic doesn't reach
    // GA4/Pixel. We gate on `import.meta.hot` (defined only while the dev
    // server runs), NOT `import.meta.env.PROD`: in this repo PROD is derived
    // from NODE_ENV, and `vite build` with an unset NODE_ENV folds PROD→false
    // and dead-code-eliminates this whole integration (a silent no-analytics
    // trap). `import.meta.hot` is serve-vs-build and NODE_ENV-independent, so
    // the loaders always bundle in a production build and only the dev server
    // no-ops.
    if (import.meta.hot) return;

    if (!initialized.current) {
      initialized.current = true;
      // First load: inject the loaders. loadPixel() fires the initial Pixel
      // PageView; GA4 is configured with send_page_view:false, so we send its
      // first page_view manually here — one PageView per surface, no double.
      loadGtag();
      loadPixel();
      trackGaPageView(location);
      return;
    }

    // Subsequent client-side navigation.
    trackGaPageView(location);
    trackPixelPageView();
  }, [location]);

  return null;
}
